/*
 * The client activity timeline: a left rail with the actor, one prose line
 * per event, Clarify-style change sub-lines, expandable batches and groups,
 * live highlight for streamed rows and cursor-paged infinite scroll.
 */
import {
  ArrowRight,
  ArrowUpRight,
  ChevronDown,
  ChevronRight,
  History,
  RefreshCw
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode
} from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/components/ui/collapsible";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from "@/components/ui/empty";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger
} from "@/components/ui/hover-card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Tag } from "@/components/ui/tag";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { AgentMoodAvatar } from "@/features/agents/components/AgentMoodAvatar";
import { cn } from "@/lib/utils";

import type { ForeignNationalActivityLogEvent } from "../../../../api/types";
import {
  absoluteTimeLabel,
  countLabel,
  describeEvent,
  groupActivity,
  iconForActorKind,
  initialsFromLabel,
  normalizeEvent,
  relativeTimeAgo,
  sectionByDay,
  toTitleCase,
  verbFor,
  type ActivityRenderItem,
  type ActorInfo,
  type ChangeLine,
  type DescribedEvent,
  type UserProfileGetter
} from "./activityModel";

export type ActivityFeedProps = {
  events: ForeignNationalActivityLogEvent[];
  getUserProfile: UserProfileGetter;
  navigate: (href: string) => void;
  isLoading: boolean;
  error?: string | null;
  onRetry?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  /** Rows that just arrived over the stream; rendered with a brief highlight. */
  liveIds?: ReadonlySet<string>;
  /** Overview card mode: ten rows, no paging, a "View all" link. */
  compact?: boolean;
  onViewAll?: () => void;
  emptyTitle?: string;
  emptyDescription?: string;
  /**
   * Practice-wide feeds: which client a row belongs to, shown as a link
   * before the sentence ("Maria Garcia · Andrew uploaded passport.pdf").
   */
  clientTag?: (ev: ForeignNationalActivityLogEvent) => ClientTag | null;
  /** Change lines shown before "+n more" (narrow rails pass fewer). */
  maxChangeLines?: number;
  /**
   * `rail`: actor and time on the first line, the sentence under it (the
   * Overview's side rail). `timeline`: one line per event with a 16px avatar
   * (the Activity tab).
   */
  layout?: "rail" | "timeline";
  className?: string;
};

export type ClientTag = { label: string; href: string };

const COMPACT_LIMIT = 10;
const SHOWN_CHANGE_LINES = 6;
const SKELETON_ROWS = 6;

// Atoms -----------------------------------------------------------------------

/** The acting assistant's seed: its case (scoped by the individual) or client. */
const agentSeedOf = (ev: ForeignNationalActivityLogEvent): string => {
  const caseId = (ev.individual_id ?? "").trim();
  return caseId ? `case:${caseId}` : `client:${ev.foreign_national_id}`;
};

const RailDisk: FC<{
  actor: ActorInfo;
  event: ForeignNationalActivityLogEvent;
  small?: boolean;
}> = ({ actor, event, small }) => {
  const ActorIcon = iconForActorKind(actor.kind);
  if (small) {
    return actor.kind === "user" ? (
      <Avatar size="2xs" className="mt-0.5">
        {actor.avatarUrl ? <AvatarImage src={actor.avatarUrl} alt="" /> : null}
        <AvatarFallback>{initialsFromLabel(actor.label)}</AvatarFallback>
      </Avatar>
    ) : actor.kind === "agent" ? (
      <AgentMoodAvatar
        size="xs"
        className="mt-0.5"
        agent={{ key: agentSeedOf(event) }}
      />
    ) : (
      <span
        className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full bg-fill-2 text-fg-3"
        aria-hidden
      >
        <ActorIcon className="size-2.5" />
      </span>
    );
  }
  return (
    <span className="relative flex size-7 shrink-0 items-center justify-center">
      {actor.kind === "user" ? (
        <Avatar size="md">
          {actor.avatarUrl ? (
            <AvatarImage src={actor.avatarUrl} alt="" />
          ) : null}
          <AvatarFallback className="text-xs font-medium">
            {initialsFromLabel(actor.label)}
          </AvatarFallback>
        </Avatar>
      ) : actor.kind === "agent" ? (
        <AgentMoodAvatar
          animated
          size="md"
          className="size-7"
          agent={{ key: agentSeedOf(event) }}
        />
      ) : (
        <span
          className="flex size-7 items-center justify-center rounded-lg border border-border bg-muted text-muted-foreground"
          aria-hidden
        >
          <ActorIcon className="size-3.5" />
        </span>
      )}
    </span>
  );
};

const TimeAgo: FC<{ value?: string | null; className?: string }> = ({
  value,
  className
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <time
        dateTime={value ?? undefined}
        className={cn(
          "shrink-0 text-xs tabular-nums text-muted-foreground",
          className
        )}
      >
        {relativeTimeAgo(value)}
      </time>
    </TooltipTrigger>
    <TooltipContent side="left">{absoluteTimeLabel(value)}</TooltipContent>
  </Tooltip>
);

/** "· 3d ago" at the end of a timeline sentence. */
const InlineTime: FC<{ value?: string | null }> = ({ value }) => (
  <span className="whitespace-nowrap text-fg-4">
    {" "}
    · <TimeAgo value={value} className="text-sm text-fg-4" />
  </span>
);

const SubjectLink: FC<{
  described: DescribedEvent;
  navigate: (href: string) => void;
}> = ({ described, navigate }) => {
  const { subject, target } = described;
  const label = (
    <span className="font-medium text-foreground">{subject.label}</span>
  );
  if (!subject.href) return label;
  return (
    <HoverCard openDelay={350} closeDelay={80}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          onClick={() => navigate(subject.href as string)}
          aria-label={`Open ${subject.label}`}
          className="inline-flex max-w-full items-center gap-0.5 rounded-sm text-left font-medium wrap-anywhere text-foreground underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
        >
          {subject.label}
          <ArrowUpRight className="size-3 text-muted-foreground" aria-hidden />
        </button>
      </HoverCardTrigger>
      <HoverCardContent align="start" className="w-64 space-y-2 p-3">
        <div className="text-sm font-medium text-fg-3">
          {toTitleCase(subject.kind)}
        </div>
        <div className="truncate text-sm font-medium">{subject.label}</div>
        {target?.label ? <Badge variant="outline">{target.label}</Badge> : null}
        <Button
          size="xs"
          variant="outline"
          className="w-full"
          onClick={() => navigate(subject.href as string)}
        >
          Open <ArrowRight />
        </Button>
      </HoverCardContent>
    </HoverCard>
  );
};

// Quiet text links for the context line: muted until hovered, never a badge.
const CONTEXT_LINK =
  "truncate rounded-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none";

const CaseTarget: FC<{
  target: DescribedEvent["target"];
  navigate: (href: string) => void;
}> = ({ target, navigate }) => {
  if (!target) return null;
  if (!target.href) return <Tag className="align-middle">{target.label}</Tag>;
  return (
    <button
      type="button"
      onClick={() => navigate(target.href as string)}
      aria-label={`Open ${target.label} case`}
      className="inline-flex max-w-full rounded-xs align-middle hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
    >
      <Tag>{target.label}</Tag>
    </button>
  );
};

const ClientTagLink: FC<{
  tag: ClientTag | null | undefined;
  navigate: (href: string) => void;
}> = ({ tag, navigate }) => {
  if (!tag) return null;
  return (
    <button
      type="button"
      onClick={() => navigate(tag.href)}
      className={CONTEXT_LINK}
      data-testid="activity-client-tag"
    >
      {tag.label}
    </button>
  );
};

/**
 * The line under the sentence that says where this happened: the client (on
 * practice-wide feeds) and the case. Renders nothing when there is neither.
 */
const ContextLine: FC<{
  clientTag?: ClientTag | null;
  target: DescribedEvent["target"];
  navigate: (href: string) => void;
}> = ({ clientTag, target, navigate }) => {
  if (!clientTag && !target) return null;
  return (
    <p className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
      <ClientTagLink tag={clientTag} navigate={navigate} />
      {clientTag && target ? <span aria-hidden>·</span> : null}
      <CaseTarget target={target} navigate={navigate} />
    </p>
  );
};

// An agent (Case Assistant / Client Manager) is tied to the client, not to the
// teammate who prompted it, so we don't attribute it "for {teammate}"; the
// client and case are carried by the context line below the sentence.
const ActorName: FC<{ actor: ActorInfo }> = ({ actor }) => (
  <span className="min-w-0 truncate font-medium text-foreground">
    {actor.label}
  </span>
);

/** The actor on the left, the relative time on the right: the row's first line. */
const MetaLine: FC<{ actor: ActorInfo; occurredAt?: string | null }> = ({
  actor,
  occurredAt
}) => (
  <div className="flex items-baseline gap-2 text-xs leading-4.5">
    <ActorName actor={actor} />
    <TimeAgo value={occurredAt} className="ml-auto" />
  </div>
);

// The kind of change is carried by colour (and strike-through for what went
// away); the word itself is read out to screen readers only.
const CHANGE_KIND_STYLES: Record<ChangeLine["kind"], string> = {
  added: "text-success",
  updated: "text-foreground",
  removed: "text-destructive line-through"
};

const CHANGE_KIND_LABELS: Record<ChangeLine["kind"], string> = {
  added: "Added",
  updated: "Updated",
  removed: "Removed"
};

/** One `Field  value` row of the change grid: label column fixed, value wraps. */
const ChangeLineRow: FC<{ line: ChangeLine }> = ({ line }) => {
  const word = CHANGE_KIND_LABELS[line.kind];
  return (
    <li
      // Label column is 88px in the rail; it widens once the row has room.
      className="grid grid-cols-[78px_minmax(0,1fr)] gap-x-2 text-xs leading-4.5 @sm:grid-cols-[132px_minmax(0,1fr)]"
      data-testid="change-line"
    >
      <span className="truncate text-muted-foreground">{line.field}</span>
      <span className="min-w-0 wrap-anywhere">
        {line.value ? (
          <>
            <span className="sr-only">{word} </span>
            <span className={CHANGE_KIND_STYLES[line.kind]}>{line.value}</span>
          </>
        ) : (
          <span className="text-muted-foreground">{word}</span>
        )}
        {line.kind === "updated" && line.oldValue ? (
          <>
            {" "}
            <span className="text-muted-foreground line-through">
              {line.oldValue}
            </span>
          </>
        ) : null}
      </span>
    </li>
  );
};

const ChangeLines: FC<{ lines: ChangeLine[]; max?: number }> = ({
  lines,
  max = SHOWN_CHANGE_LINES
}) => {
  const [open, setOpen] = useState(false);
  if (lines.length === 0) return null;
  const shown = lines.slice(0, max);
  const hidden = lines.slice(max);
  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="mt-1.5 @container"
    >
      <ul className="space-y-1">
        {shown.map((line, i) => (
          <ChangeLineRow key={`${line.field}-${i}`} line={line} />
        ))}
      </ul>
      {hidden.length > 0 ? (
        <>
          <CollapsibleContent>
            <ul className="mt-1 space-y-1">
              {hidden.map((line, i) => (
                <ChangeLineRow key={`${line.field}-h-${i}`} line={line} />
              ))}
            </ul>
          </CollapsibleContent>
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              size="xs"
              className="mt-1 -ml-1 h-auto px-0 py-0 text-xs leading-4.5 font-normal text-muted-foreground hover:bg-transparent hover:text-foreground [&_svg]:size-3"
            >
              {open ? "Show Less" : `+${hidden.length} More`}
              <ChevronDown
                className={cn("transition-transform", open && "rotate-180")}
              />
            </Button>
          </CollapsibleTrigger>
        </>
      ) : null}
    </Collapsible>
  );
};

const BatchItems: FC<{
  described: DescribedEvent;
  navigate: (href: string) => void;
}> = ({ described, navigate }) => {
  const [open, setOpen] = useState(false);
  const { batchItems } = described;
  if (batchItems.length === 0) return null;
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mt-0.5">
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          size="xs"
          className="-ml-1 h-auto px-0 py-0 text-xs leading-4.5 font-normal text-muted-foreground hover:bg-transparent hover:text-foreground [&_svg]:size-3"
          aria-label={open ? "Hide items" : "Show items"}
        >
          <ChevronRight
            className={cn("transition-transform", open && "rotate-90")}
          />
          {open ? "Hide" : "Show"} {batchItems.length}{" "}
          {batchItems.length === 1 ? "Item" : "Items"}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul className="mt-1 space-y-1 border-l border-border pl-3">
          {batchItems.map((item) => (
            <li
              key={item.id}
              className="flex min-w-0 items-center gap-1.5 text-xs"
              data-testid="batch-item"
            >
              {item.href ? (
                <button
                  type="button"
                  onClick={() => navigate(item.href as string)}
                  className="truncate underline-offset-4 hover:underline"
                >
                  {item.label}
                </button>
              ) : (
                <span className="truncate">{item.label}</span>
              )}
            </li>
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
};

// Rows ------------------------------------------------------------------------

type RowShellProps = {
  rail: ReactNode;
  /** The timeline's 16px rail: tighter rows, connector from the small disk. */
  dense?: boolean;
  live?: boolean;
  last?: boolean;
  testId?: string;
  toggleDetails?: () => void;
  detailsExpanded?: boolean;
  children: ReactNode;
};

/** The timeline row: rail disk on the left, connector line, content on the right. */
const RowShell: FC<RowShellProps> = ({
  rail,
  dense,
  live,
  last,
  testId,
  toggleDetails,
  detailsExpanded,
  children
}) => {
  const canToggle = Boolean(toggleDetails);
  const rowRef = useRef<HTMLLIElement | null>(null);
  const lastToggleAtRef = useRef(0);
  const ignoreNestedControl = (target: EventTarget | null): boolean =>
    target instanceof HTMLElement &&
    Boolean(target.closest("button, a, input, select, textarea"));
  const toggleFromPress = (target: EventTarget | null, timeStamp: number) => {
    if (!toggleDetails || ignoreNestedControl(target)) return;
    if (lastToggleAtRef.current && timeStamp - lastToggleAtRef.current < 100)
      return;
    lastToggleAtRef.current = timeStamp;
    toggleDetails();
  };

  useEffect(() => {
    const node = rowRef.current;
    if (!node || !toggleDetails) return undefined;
    const onPress = (event: Event) =>
      toggleFromPress(event.target, event.timeStamp);
    node.addEventListener("pointerdown", onPress, { capture: true });
    node.addEventListener("mousedown", onPress, { capture: true });
    return () => {
      node.removeEventListener("pointerdown", onPress, { capture: true });
      node.removeEventListener("mousedown", onPress, { capture: true });
    };
  }, [toggleDetails]);

  return (
    <li
      ref={rowRef}
      data-testid={testId}
      data-live={live ? "true" : undefined}
      data-expanded={canToggle ? String(Boolean(detailsExpanded)) : undefined}
      className={cn(
        "relative flex rounded-md px-1 transition-colors duration-700",
        dense ? "gap-2.5 py-1" : "gap-2 py-1.5",
        canToggle && "cursor-pointer hover:bg-fill-1",
        live && "bg-accent"
      )}
    >
      {/*
        Rail column: the disk plus the connector to the next row. The line is
        centred on the column itself (`left-1/2`) rather than at a fixed offset
        from the row edge, so it stays under the disk's midpoint regardless of
        the row's padding. It starts at the disk's bottom edge (28px) and ends
        at the next row's disk top: this row's 6px bottom padding + the next
        row's 6px top padding.
      */}
      <div className="relative flex shrink-0 flex-col items-center">
        {rail}
        {!last ? (
          <span
            aria-hidden
            className={cn(
              "absolute left-1/2 w-px -translate-x-1/2",
              dense ? "top-5 -bottom-2 bg-line-2" : "top-7 -bottom-3 bg-border"
            )}
          />
        ) : null}
      </div>
      <div className="min-w-0 flex-1">{children}</div>
    </li>
  );
};

const EventRow: FC<{
  ev: ForeignNationalActivityLogEvent;
  getUserProfile: UserProfileGetter;
  navigate: (href: string) => void;
  live?: boolean;
  last?: boolean;
  compact?: boolean;
  clientTag?: ClientTag | null;
  maxChangeLines?: number;
  timeline?: boolean;
}> = ({
  ev,
  getUserProfile,
  navigate,
  live,
  last,
  compact,
  clientTag,
  maxChangeLines,
  timeline
}) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const described = useMemo(
    () => describeEvent(ev, getUserProfile),
    [ev, getUserProfile]
  );
  const hasDiffs = !compact && described.changeLines.length > 0;
  return (
    <RowShell
      rail={<RailDisk actor={described.actor} event={ev} small={timeline} />}
      dense={timeline}
      live={live}
      last={last}
      testId="activity-row"
      toggleDetails={hasDiffs ? () => setDetailsOpen((v) => !v) : undefined}
      detailsExpanded={detailsOpen}
    >
      {timeline ? (
        <p className="text-base leading-5 text-fg-3">
          <ClientTagLink tag={clientTag} navigate={navigate} />
          {clientTag ? " · " : null}
          <ActorName actor={described.actor} /> <span>{described.verb}</span>{" "}
          <SubjectLink described={described} navigate={navigate} />
          {described.target ? " " : null}
          <CaseTarget target={described.target} navigate={navigate} />
          <InlineTime value={ev.occurred_at} />
        </p>
      ) : (
        <>
          <MetaLine actor={described.actor} occurredAt={ev.occurred_at} />
          {/* Inline prose, so it wraps like a sentence rather than a stack of tokens. */}
          <p className="text-xs leading-4.5 text-muted-foreground">
            <span>{described.verb}</span>{" "}
            <SubjectLink described={described} navigate={navigate} />
          </p>
          <ContextLine
            clientTag={clientTag}
            target={described.target}
            navigate={navigate}
          />
        </>
      )}
      {!compact ? (
        <>
          {detailsOpen ? (
            <ChangeLines lines={described.changeLines} max={maxChangeLines} />
          ) : null}
          <BatchItems described={described} navigate={navigate} />
        </>
      ) : null}
    </RowShell>
  );
};

const GroupRow: FC<{
  item: Extract<ActivityRenderItem, { type: "group" }>;
  getUserProfile: UserProfileGetter;
  navigate: (href: string) => void;
  liveIds?: ReadonlySet<string>;
  last?: boolean;
  compact?: boolean;
  clientTag?: ClientTag | null;
  timeline?: boolean;
}> = ({
  item,
  getUserProfile,
  navigate,
  liveIds,
  last,
  compact,
  clientTag,
  timeline
}) => {
  const [open, setOpen] = useState(false);
  const first = item.events[0];
  const described = useMemo(
    () => describeEvent(first, getUserProfile),
    [first, getUserProfile]
  );
  const n = normalizeEvent(first);
  const noun = countLabel(item.count, n.entity_kind).replace(/^\d+\s+/, "");
  const live = item.events.some((ev) => liveIds?.has(ev.activity_id));
  return (
    <RowShell
      rail={<RailDisk actor={described.actor} event={first} small={timeline} />}
      dense={timeline}
      live={live}
      last={last}
      testId="activity-group-row"
    >
      <Collapsible open={open} onOpenChange={setOpen}>
        {timeline ? (
          <p className="text-base leading-5 text-fg-3">
            <ClientTagLink tag={clientTag} navigate={navigate} />
            {clientTag ? " · " : null}
            <ActorName actor={described.actor} />{" "}
            <span>{verbFor(n.action)}</span>{" "}
            <span className="text-fg">
              {item.count} {noun}
            </span>
            {described.target ? " " : null}
            <CaseTarget target={described.target} navigate={navigate} />
            <InlineTime value={first.occurred_at} />
          </p>
        ) : (
          <>
            <MetaLine actor={described.actor} occurredAt={first.occurred_at} />
            <p className="text-xs leading-4.5 text-muted-foreground">
              <span>{verbFor(n.action)}</span>{" "}
              <span className="font-medium text-foreground">
                {item.count} {noun}
              </span>
            </p>
            <ContextLine
              clientTag={clientTag}
              target={described.target}
              navigate={navigate}
            />
          </>
        )}
        {!compact ? (
          <>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="xs"
                className="mt-0.5 -ml-1 h-auto px-0 py-0 text-xs leading-4.5 font-normal text-muted-foreground hover:bg-transparent hover:text-foreground [&_svg]:size-3"
                aria-expanded={open}
                aria-label={open ? "Collapse group" : "Expand group"}
              >
                <ChevronRight
                  className={cn("transition-transform", open && "rotate-90")}
                />
                {open ? "Hide" : "Show"} {item.events.length} Events
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <ul className="mt-1 space-y-1.5 border-l border-border pl-3">
                {item.events.map((ev) => {
                  const d = describeEvent(ev, getUserProfile);
                  return (
                    <li
                      key={ev.activity_id}
                      data-testid="activity-group-child"
                      className="flex items-start justify-between gap-3 text-xs"
                    >
                      <span className="flex min-w-0 flex-wrap items-center gap-x-1">
                        <span className="text-muted-foreground">{d.verb}</span>
                        <SubjectLink described={d} navigate={navigate} />
                      </span>
                      <TimeAgo value={ev.occurred_at} />
                    </li>
                  );
                })}
              </ul>
            </CollapsibleContent>
          </>
        ) : null}
      </Collapsible>
    </RowShell>
  );
};

// States ----------------------------------------------------------------------

const SkeletonRows: FC<{ count?: number }> = ({ count = SKELETON_ROWS }) => (
  <ul className="space-y-1" aria-busy data-testid="activity-skeleton">
    {Array.from({ length: count }, (_, i) => (
      <li key={i} className="relative flex gap-3 px-2 py-2.5">
        <Skeleton className="size-8 shrink-0 rounded-lg" />
        <div className="flex-1 space-y-2 pt-1">
          <div className="flex items-center justify-between gap-3">
            <Skeleton className="h-3.5 w-[60%]" />
            <Skeleton className="h-3 w-12" />
          </div>
          {i % 2 === 0 ? <Skeleton className="h-3 w-[40%]" /> : null}
        </div>
      </li>
    ))}
  </ul>
);

const FeedEmpty: FC<{
  title: string;
  description?: string;
  error?: boolean;
  onRetry?: () => void;
}> = ({ title, description, error, onRetry }) => (
  <Empty className="py-10">
    <EmptyHeader>
      <EmptyMedia variant="icon">
        <History />
      </EmptyMedia>
      <EmptyTitle>{title}</EmptyTitle>
      {description ? <EmptyDescription>{description}</EmptyDescription> : null}
    </EmptyHeader>
    {error && onRetry ? (
      <Button variant="outline" size="sm" onClick={onRetry}>
        <RefreshCw /> Try again
      </Button>
    ) : null}
  </Empty>
);

// Feed ------------------------------------------------------------------------

/** Clarify-style timeline of a client's activity. */
export const ActivityFeed: FC<ActivityFeedProps> = ({
  events,
  getUserProfile,
  navigate,
  isLoading,
  error,
  onRetry,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  liveIds,
  compact = false,
  onViewAll,
  emptyTitle = "No activity yet.",
  emptyDescription = "Changes to this client, their cases and documents will show up here.",
  clientTag,
  maxChangeLines = SHOWN_CHANGE_LINES,
  layout = "rail",
  className
}) => {
  const timeline = layout === "timeline";
  const visible = useMemo(
    () => (compact ? events.slice(0, COMPACT_LIMIT) : events),
    [compact, events]
  );
  const sections = useMemo(
    () => sectionByDay(groupActivity(visible)),
    [visible]
  );

  // Infinite scroll: ask for more when the sentinel scrolls into view.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || compact || !hasMore || !onLoadMore) return undefined;
    if (typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && !isLoadingMore) onLoadMore();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [compact, hasMore, isLoadingMore, onLoadMore]);

  const isEmpty = !isLoading && !error && visible.length === 0;
  const lastId = useMemo(() => {
    const lastSection = sections[sections.length - 1];
    const lastItem = lastSection?.items[lastSection.items.length - 1];
    if (!lastItem) return null;
    return lastItem.type === "event" ? lastItem.ev.activity_id : lastItem.key;
  }, [sections]);

  return (
    <ScrollArea
      className={cn(
        "h-full min-h-0 w-full",
        // Radix renders the viewport's child as `display: table`, which grows
        // to fit an unbreakable filename and pushes the rows out of view. Make
        // it a block so the rows wrap to the rail instead.
        "[&_[data-slot=scroll-area-viewport]>div]:block!",
        className
      )}
      data-testid="activity-feed"
    >
      <div className={cn("px-1 pb-2", !compact && "pr-3")}>
        {isLoading && visible.length === 0 ? (
          <SkeletonRows count={compact ? 4 : SKELETON_ROWS} />
        ) : error && visible.length === 0 ? (
          <FeedEmpty
            title="Couldn't load activity."
            description={error}
            error
            onRetry={onRetry}
          />
        ) : isEmpty ? (
          <FeedEmpty title={emptyTitle} description={emptyDescription} />
        ) : (
          sections.map((section, idx) => (
            <div
              key={section.key}
              role="group"
              aria-label={section.label}
              className={cn(idx > 0 && "mt-4")}
            >
              <h3 className="sticky top-0 z-10 -ml-1 -mr-3 bg-surface py-1.5 pr-3 pl-2 text-sm font-medium text-fg-3">
                {section.label}
              </h3>
              <ul>
                {section.items.map((item) =>
                  item.type === "group" ? (
                    <GroupRow
                      key={item.key}
                      item={item}
                      getUserProfile={getUserProfile}
                      navigate={navigate}
                      liveIds={liveIds}
                      last={item.key === lastId && !hasMore}
                      compact={compact}
                      clientTag={clientTag?.(item.events[0])}
                      timeline={timeline}
                    />
                  ) : (
                    <EventRow
                      key={item.ev.activity_id}
                      ev={item.ev}
                      getUserProfile={getUserProfile}
                      navigate={navigate}
                      live={liveIds?.has(item.ev.activity_id)}
                      last={item.ev.activity_id === lastId && !hasMore}
                      compact={compact}
                      clientTag={clientTag?.(item.ev)}
                      maxChangeLines={maxChangeLines}
                      timeline={timeline}
                    />
                  )
                )}
              </ul>
            </div>
          ))
        )}

        {compact ? (
          onViewAll && events.length > 0 ? (
            <div className="flex justify-center pt-2">
              <Button variant="ghost" size="sm" onClick={onViewAll}>
                View all activity <ArrowRight />
              </Button>
            </div>
          ) : null
        ) : hasMore ? (
          <div className="flex flex-col items-center gap-2 pt-2">
            <div ref={sentinelRef} className="h-px w-full" aria-hidden />
            <Button
              variant="ghost"
              size="sm"
              disabled={isLoadingMore}
              onClick={onLoadMore}
            >
              {isLoadingMore ? "Loading…" : "Load More"}
            </Button>
          </div>
        ) : null}
      </div>
    </ScrollArea>
  );
};
